import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/base/PageLayout';
import HubCards from '../../components/feature/HubCards';

const Services: React.FC = () => {
  useEffect(() => {
    document.title = 'サービス一覧 - 企業研修・マナー講座・プライベート執事｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが提供する各種サービス。企業向けホスピタリティ研修、個人向けマナー講座、プライベート執事サービス、コンサルティングなど、目的に合わせた多彩なプログラムをご用意。全日本執事協会会長として培った専門知識と実践経験を活かし、本格的な執事教育とサービスを提供いたします。');
    }

    // Add Keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'サービス一覧, 企業研修, マナー講座, プライベート執事, コンサルティング');
    } else {
      const keywordsTag = document.createElement('meta');
      keywordsTag.name = 'keywords';
      keywordsTag.content = 'サービス一覧, 企業研修, マナー講座, プライベート執事, コンサルティング';
      document.head.appendChild(keywordsTag);
    }

    // Add Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/services`);
    } else {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/services`;
      document.head.appendChild(canonical);
    }

    // Add Last-modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    } else {
      const lastModTag = document.createElement('meta');
      lastModTag.name = 'last-modified';
      lastModTag.content = new Date().toISOString().split('T')[0];
      document.head.appendChild(lastModTag);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "執事サービス・マナー研修",
      "provider": {
        "@type": "Person",
        "name": "執事ベル",
        "jobTitle": "全日本執事協会 3代目会長"
      },
      "areaServed": {
        "@type": "Country",
        "name": "日本"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "執事サービス一覧",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "企業研修・セミナー",
              "description": "ホスピタリティとマナーをテーマにした研修プログラム"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "プライベート執事サービス",
              "description": "個人のお客様への専属執事サービス"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "マナー講座",
              "description": "個人向けの実践的なマナー指導"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "コンサルティング",
              "description": "サービス業界向けの専門コンサルティング"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      title: '企業研修・セミナー',
      description: 'ホスピタリティとマナーをテーマにした研修プログラム',
      features: [
        '接客サービス向上研修',
        'ビジネスマナー講座',
        'リーダーシップ研修',
        'チームビルディング'
      ],
      path: '/services/corporate-training',
      image: '/images/generated/seq-corporate-training-elegant-84c5a5.jpg',
      duration: '半日〜2日間'
    },
    {
      title: 'プライベート執事サービス',
      description: '個人のお客様への専属執事サービス',
      features: [
        '日常生活サポート',
        'イベント運営',
        '旅行アテンド',
        '秘書業務'
      ],
      path: '/services/private-butler',
      image: '/images/generated/seq-private-butler-elegant-b3668c.jpg',
      duration: '継続契約'
    },
    {
      title: 'マナー講座',
      description: '個人向けの実践的なマナー指導',
      features: [
        'テーブルマナー',
        'ビジネスマナー',
        '冠婚葬祭マナー',
        '国際マナー'
      ],
      path: '/services/manner-course',
      image: '/images/generated/seq-manner-course-elegant-841489.jpg',
      duration: '2時間/回'
    },
    {
      title: 'コンサルティング',
      description: 'サービス業界向けの専門コンサルティング',
      features: [
        'サービス品質向上',
        'スタッフ教育',
        'オペレーション改善',
        '顧客満足度向上'
      ],
      path: '/services/consulting',
      image: '/images/generated/seq-consulting-elegant-92f072.jpg',
      duration: '3ヶ月〜'
    }
  ];

  return (
    <PageLayout hero={<>
<section className="relative py-24 md:py-32 bg-cover bg-center" style={{
          backgroundImage: 'url(/images/generated/seq-services-hero-elegant-b80dc4.jpg)'
        }}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-3xl md:text-6xl font-serif mb-6 md:mb-8 tracking-wider">
                サービス一覧
              </h1>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6 md:mb-8"></div>
              <p className="text-base md:text-xl leading-relaxed max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
                執事文化の普及を目的とした<br className="md:hidden" />
                各種プログラムをご用意しております
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Services Grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-md shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  <div className="relative h-64 md:h-72 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                  </div>
                  <div className="p-8 md:p-10">
                    <h3 className="text-xl md:text-2xl font-serif text-gray-900 mb-4 tracking-wider">
                      {service.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="text-sm md:text-base font-medium text-gray-900 mb-3 tracking-wide">プログラム例</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start text-gray-700">
                            <div className="w-1.5 h-1.5 bg-amber-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-sm md:text-base">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200 text-sm md:text-base text-gray-600">
                      <div>
                        <span className="font-medium text-gray-900">期間：</span>
                        <span>{service.duration}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">料金：</span>
                        <span className="text-amber-700">お見積もり</span>
                      </div>
                    </div>

                    <Link
                      to={service.path}
                      className="btn-gold block w-full bg-amber-700 text-white text-center py-3 md:py-4 rounded-md font-medium hover:bg-amber-800 transition-all duration-300 whitespace-nowrap text-sm md:text-base tracking-wider shadow-md hover:shadow-lg"
                    >
                      詳細を見る
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-4 tracking-wider">
                選ばれる理由
              </h2>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6"></div>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                全日本執事協会3代目頭取として培った経験と実績
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {[
                {
                  icon: 'ri-award-line',
                  title: '豊富な実績',
                  description: '30年以上の執事経験と、数多くの企業・個人への指導実績'
                },
                {
                  icon: 'ri-user-star-line',
                  title: '個別対応',
                  description: 'お客様一人ひとりのニーズに合わせたオーダーメイドサービス'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '信頼と実績',
                  description: '全日本執事協会の権威ある立場からの確かな指導'
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: '実践的指導',
                  description: '理論だけでなく、現場で即活用できる実践的なスキル'
                },
                {
                  icon: 'ri-heart-line',
                  title: '心のこもったサービス',
                  description: '技術だけでなく、おもてなしの心を大切にした指導'
                },
                {
                  icon: 'ri-time-line',
                  title: '継続サポート',
                  description: '研修後のフォローアップと継続的な改善サポート'
                }
              ].map((reason, index) => (
                <div key={index} className="text-center bg-white p-8 rounded-md shadow-md hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-5 bg-gradient-to-br from-amber-100 to-amber-50 rounded-full">
                    <i className={`${reason.icon} text-2xl md:text-3xl text-amber-700`}></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-serif text-gray-900 mb-4 tracking-wide">
                    {reason.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed px-2">
                    {reason.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-4xl font-serif text-gray-900 mb-4 tracking-wider">
                サービスの流れ
              </h2>
              <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-6"></div>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                お問い合わせから実施まで、丁寧にサポートいたします
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-10">
              {[
                {
                  step: '01',
                  title: 'お問い合わせ',
                  description: 'まずはお気軽にご相談ください。ご要望をお聞かせいただきます。'
                },
                {
                  step: '02',
                  title: 'ヒアリング',
                  description: '詳細なニーズをお伺いし、最適なプランをご提案いたします。'
                },
                {
                  step: '03',
                  title: 'プラン作成',
                  description: 'お客様専用のカスタマイズされたプログラムを作成いたします。'
                },
                {
                  step: '04',
                  title: 'サービス実施',
                  description: '経験豊富な講師陣による質の高いサービスをご提供いたします。'
                }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-5 bg-gradient-to-br from-amber-700 to-amber-800 text-white rounded-full shadow-lg">
                    <span className="text-xl md:text-2xl font-bold tracking-wider">{process.step}</span>
                  </div>
                  <h3 className="text-lg md:text-xl font-serif text-gray-900 mb-4 tracking-wide">
                    {process.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed px-2">
                    {process.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-amber-700 via-amber-800 to-amber-700 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-4xl font-serif text-white mb-6 md:mb-8 tracking-wider">
              お気軽にお問い合わせください
            </h2>
            <div className="w-16 h-0.5 bg-white/50 mx-auto mb-6 md:mb-8"></div>
            <p className="text-base md:text-xl text-amber-50 mb-8 md:mb-10 leading-relaxed px-5 sm:px-6 lg:px-8">
              お客様のご要望に合わせた最適なサービスをご提案いたします
            </p>
            <Link
              to="/contact"
              className="btn-gold inline-block bg-white text-amber-800 px-8 md:px-10 py-4 md:py-5 rounded-md font-medium hover:bg-amber-50 transition-all duration-300 whitespace-nowrap text-sm md:text-base tracking-wider shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              お問い合わせフォーム
            </Link>
          </div>
        </section>
      </div>
          <HubCards group="services" />
    </PageLayout>
  );
};

export default Services;
